import {captureException} from '@sentry/browser';
import {useNotificationContext} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {ReactNode, useRef} from 'react';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useLazyGetMessageByIdQuery} from '@dms/api/core';
import {notificationTypes, refreshDmsDatagrid} from '@dms/shared';

import {parseDate} from 'shared/web';

import {promptBridgeNotificationsFactory} from './utils/promptBridgeNotificationsFactory';

interface PromptBridgeProviderProps {
  children: ReactNode;
}

const EXTRACTED_DELIVERY_NOTES = 'extracted-delivery-notes';

export const PromptBridgeProvider = (props: PromptBridgeProviderProps) => {
  const [getMessageById] = useLazyGetMessageByIdQuery();
  const {notifications, showNotification, updateNotification, hideNotification} =
    useNotificationContext();
  const formatDateTime = useDateTimeFormatter();

  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  useSubscribeToNotificationsQuery((notification) => {
    if (
      notification.variant !== 'toast' ||
      notification.type !== notificationTypes.PROMPT_BRIDGE_STATUS_CHANGED
    ) {
      return;
    }

    const {messageId, status} = notification.payload;

    // Refresh on every status change so the grid follows a message through PENDING and PROCESSING,
    // not only to its result. The toast below stays terminal-only.
    refreshDmsDatagrid(EXTRACTED_DELIVERY_NOTES);

    if (status !== 'SUCCESS' && status !== 'FAILED') {
      return;
    }

    getMessageById({id: messageId})
      .unwrap()
      .then((message) => {
        if (message.useCaseType !== 'extractDeliveryNoteDocument') {
          return;
        }

        promptBridgeNotificationsFactory(message.useCaseType, {
          notifications: notificationsRef.current,
          showNotification,
          updateNotification,
          hideNotification,
          notificationId: messageId,
          status: status === 'SUCCESS' ? 'success' : 'error',
          createdAt: formatDateTime('dateTimeShort', parseDate(message.createdAt)),
          payload: JSON.stringify(message.input),
        });
      })
      .catch(captureException);
  });

  return <>{props.children}</>;
};
